import { useEffect, useState } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Reads the visitor's reduced-motion preference, and keeps reading it.
 *
 * #1967 R6: the hero is an autoplaying loop. A visitor who has asked their OS
 * for reduced motion gets the poster still instead — the same picture, held.
 *
 * Guarded rather than assumed: `matchMedia` is absent in jsdom (the test
 * environment) and in any SSR pass, and an unguarded call there throws before
 * the hero renders at all. Absence is read as "no preference expressed", which
 * is the same answer a browser with no preference gives.
 */
function readPreference(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

export function useReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(readPreference);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    // Re-read on mount: the preference can change between the initial state
    // computation and the effect, and on a hydrated page the first read may
    // have happened without a window.
    setPrefersReduced(query.matches);

    const handleChange = (event: MediaQueryListEvent) => setPrefersReduced(event.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return prefersReduced;
}
